import React from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ChecklistItem, ChecklistItemStatus } from 'design-system/src/components/Checklist/ChecklistItem';
import { Checklist } from 'design-system/src/components/Checklist/Checklist';
import Button from 'design-system/src/components/Button/Button';
import { FaxVerificationOnboardingStatus, TrainingSessionOnboardingStatus } from '../../api';
import { Badge } from 'design-system/src/components/Badge/Badge';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { pluralize } from 'shared-utils/utils/formatString';
import { ScheduleTrainingWidget } from './ScheduleTrainingWidget';

type StatusBadge = {
  badgeText: string;
  tooltipText: string;
};

type OnboardingChecklistItem = {
  title: string;
  description?: string | undefined;
  getLink?: (faxVerificationStatus: FaxVerificationOnboardingStatus) => { label: string; to: string } | undefined;
  getBadge?: (
    numPrescriptionsBlocked: number,
    faxVerificationStatus: FaxVerificationOnboardingStatus,
  ) => StatusBadge | null;
  getStatus: (faxVerificationStatus: FaxVerificationOnboardingStatus | undefined) => ChecklistItemStatus;
};

type OnboardingChecklistItemKey = 'create_account' | 'receive_account_information' | 'complete_verification';

const FAX_VERIFICATION_STATUS_TO_ONBOARDING_CHECKLIST_COMPLETION: Record<
  FaxVerificationOnboardingStatus,
  ChecklistItemStatus
> = {
  awaiting_account_setup: 'pending',
  awaiting_fax_trigger: 'pending',
  awaiting_prescriber_verification: 'pending',
  awaiting_tandem_verification: 'loading',
  verification_successful: 'complete',
  verification_failed: 'pending',
};

const getStatusForCompleteVerification = (faxVerificationStatus: FaxVerificationOnboardingStatus | undefined) => {
  if (!faxVerificationStatus) {
    return 'pending';
  }
  return FAX_VERIFICATION_STATUS_TO_ONBOARDING_CHECKLIST_COMPLETION[faxVerificationStatus];
};

const getBadgeForCompleteVerification = (
  numPrescriptionsBlocked: number,
  faxVerificationStatus: FaxVerificationOnboardingStatus,
) => {
  const prescriptionsString = pluralize(numPrescriptionsBlocked, 'prescription');
  const inProgressTooltipText = `We have received your fax information and will complete your verification within 48 hours.`;
  const verificationInProgress = faxVerificationStatus === 'awaiting_tandem_verification';

  if (numPrescriptionsBlocked && numPrescriptionsBlocked > 1) {
    const badgeText = `${numPrescriptionsBlocked} Rx in Queue`;
    let tooltipText = '';
    if (verificationInProgress) {
      tooltipText = `${inProgressTooltipText} Your ${numPrescriptionsBlocked} ${prescriptionsString} will be processed once that's done.`;
    } else {
      tooltipText = `You have ${numPrescriptionsBlocked} ${prescriptionsString} in the queue. We will process them once your verification is complete (it can take up to 48 hours).`;
    }
    return {
      badgeText: badgeText,
      tooltipText: tooltipText,
    };
  } else if (verificationInProgress) {
    return {
      badgeText: 'In Progress',
      tooltipText: inProgressTooltipText,
    };
  } else {
    // If no prescriptions are blocked and we're not awaiting tandem verification, we don't need to show a badge
    return null;
  }
};

const STATUSES_BEFORE_ACCOUNT_READY_FOR_VERIFICATION = ['awaiting_account_setup', 'awaiting_prescriber_verification'];

const ONBOARDING_CHECKLIST_ITEM_MAP: Record<OnboardingChecklistItemKey, OnboardingChecklistItem> = {
  create_account: {
    title: 'Create your account',
    getStatus: () => 'complete', // Always complete,
  },
  receive_account_information: {
    title: "We're configuring your account",
    description: "You'll receive an email with next steps as soon as it's ready.",
    getStatus: (faxVerificationStatus) =>
      STATUSES_BEFORE_ACCOUNT_READY_FOR_VERIFICATION.includes(faxVerificationStatus ?? '') ? 'pending' : 'complete',
  },
  complete_verification: {
    title: 'Complete verification to start processing PAs',
    description: 'Follow the task instructions to complete verification and begin submitting e-prescriptions.',
    getStatus: getStatusForCompleteVerification,
    getBadge: (numPrescriptionsBlocked: number, faxVerificationStatus: FaxVerificationOnboardingStatus) =>
      getBadgeForCompleteVerification(numPrescriptionsBlocked, faxVerificationStatus),
    getLink: (faxVerificationStatus: FaxVerificationOnboardingStatus) =>
      faxVerificationStatus === 'awaiting_fax_trigger'
        ? {
            label: 'Verify fax number',
            to: '/verify-fax',
          }
        : undefined,
  },
};

type OnboardingChecklistItemProps = {
  title: string;
  badge?: StatusBadge;
  description?: string | undefined;
  link?: { label: string; to: string } | undefined;
  status: ChecklistItemStatus;
};

const OnboardingChecklistItem = ({ title, description, link, status, badge }: OnboardingChecklistItemProps) => {
  return (
    <ChecklistItem status={status}>
      <div className="mb-1 flex flex-row items-center gap-2 max-lg:flex-col max-lg:items-start max-lg:gap-1">
        <Typography weight={600}>{title}</Typography>
        {badge && (
          <Tooltip content={badge.tooltipText}>
            <Badge variant="neutral" slim>
              <Typography styledAs="bodyExtraSmallSpaceGrotesk" weight={500} color="var(--grayscale-600)">
                {badge.badgeText}
              </Typography>
            </Badge>
          </Tooltip>
        )}
      </div>

      <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
        {description}
      </Typography>
      {link && (
        <div className="mt-2">
          <Button to={link.to} size="xs">
            {link.label}
          </Button>
        </div>
      )}
    </ChecklistItem>
  );
};

export const OnboardingHeroBanner = ({
  faxVerificationStatus,
  numPrescriptionsBlocked,
  trainingSessionStatus,
}: {
  faxVerificationStatus: FaxVerificationOnboardingStatus;
  numPrescriptionsBlocked: number;
  trainingSessionStatus: TrainingSessionOnboardingStatus;
}) => {
  return (
    <div className="flex items-start overflow-hidden rounded-lg border border-solid border-grayscale-100 bg-white max-md:flex-col">
      <div className="flex flex-1 flex-col gap-6 p-6">
        <Typography styledAs="h6" renderedAs="h2">
          What happens next
        </Typography>
        <Checklist>
          {Object.values(ONBOARDING_CHECKLIST_ITEM_MAP).map((item) => {
            const badge = item.getBadge?.(numPrescriptionsBlocked, faxVerificationStatus);
            const status = item.getStatus(faxVerificationStatus);
            const link = item.getLink?.(faxVerificationStatus);
            return (
              <OnboardingChecklistItem
                key={item.title}
                title={item.title}
                description={item.description}
                link={link}
                status={status}
                badge={badge ?? undefined}
              />
            );
          })}
        </Checklist>
      </div>
      {(trainingSessionStatus === 'awaiting_booking' || trainingSessionStatus === 'booked') && (
        <div className="flex flex-1 flex-col items-center justify-center self-stretch rounded-lg border border-solid border-grayscale-50 bg-surface-100 p-6">
          <ScheduleTrainingWidget trainingSessionStatus={trainingSessionStatus} />
        </div>
      )}
    </div>
  );
};
